import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ChartProvider, { useChartContext } from '../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../charts/echarts/ColorProvider'
import AxisX from '../../../charts/echarts/AxisX'
import AxisY from '../../../charts/echarts/AxisY'
import Tooltip, { formatTemplate } from '../../../charts/echarts/Tooltip'
import ComponentBar from '../../../charts/echarts/ComponentBar'
import ComponentLine from '../../../charts/echarts/ComponentLine'
import ComponentScatter from '../../../charts/echarts/ComponentScatter'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import useBlueprintData from '../hooks/useBlueprintData'
import { buildLegendConfig, buildLabelConfig, EMPTY_OBJ } from './chartLayoutUtils'

const SERIES_COMPONENT_MAP = {
  bar: ComponentBar,
  line: ComponentLine,
  scatter: ComponentScatter
}

function useSeriesData (data, xAccessor, series) {
  return useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { points: {}, sizeInfo: {} }
    const points = {}
    const sizeInfo = {}
    for (const s of series) {
      const { points: seriesPoints, sizeInfo: seriesSizeInfo } = mapSeriesPoints(data, xAccessor, s)
      points[s.seriesId] = seriesPoints
      if (seriesSizeInfo) sizeInfo[s.seriesId] = seriesSizeInfo
    }
    return { points, sizeInfo }
  }, [data, xAccessor, series])
}

const SCALING_MODES = new Set(['linear', 'log', 'exp', 'outlier'])

function computeSymbolSizeFn (symbolSize, sizeMin, sizeMax, info) {
  if (!SCALING_MODES.has(symbolSize)) return typeof symbolSize === 'number' ? symbolSize : undefined
  if (!info) return sizeMin ?? 5

  const effectiveMin = sizeMin ?? 5
  const effectiveMax = sizeMax ?? 50
  const range = effectiveMax - effectiveMin

  if (symbolSize === 'linear') {
    const [dataMin, dataMax] = info.extent
    const domain = dataMax - dataMin
    if (domain === 0) return effectiveMin
    return val => {
      if (val[2] == null) return effectiveMin
      const t = (val[2] - dataMin) / domain
      return effectiveMin + t * range
    }
  }

  if (symbolSize === 'log') {
    const [dataMin, dataMax] = info.extent
    const logMin = Math.log10(Math.max(dataMin, 1e-10))
    const logMax = Math.log10(Math.max(dataMax, 1e-10))
    const logDomain = logMax - logMin
    if (logDomain === 0) return effectiveMin
    return val => {
      if (val[2] == null) return effectiveMin
      const logVal = Math.log10(Math.max(val[2], 1e-10))
      const t = (logVal - logMin) / logDomain
      return effectiveMin + t * range
    }
  }

  if (symbolSize === 'exp') {
    const [dataMin, dataMax] = info.extent
    const domain = dataMax - dataMin
    if (domain === 0) return effectiveMin
    return val => {
      if (val[2] == null) return effectiveMin
      const t = (val[2] - dataMin) / domain
      const tExp = (Math.exp(t) - 1) / (Math.E - 1)
      return effectiveMin + tExp * range
    }
  }

  if (symbolSize === 'outlier') {
    const { mean, std } = info.stats
    const maxZ = 3
    return val => {
      if (val[2] == null) return effectiveMin
      const z = std ? (val[2] - mean) / std : 0
      const t = Math.min(Math.abs(z) / maxZ, 1)
      return effectiveMin + t * range
    }
  }

  return undefined
}

function mapSeriesPoints (data, xAccessor, seriesConfig) {
  const hasSizeAccessor = seriesConfig.type === 'scatter' && seriesConfig.sizeAccessor
  // nameAccessor sets the per-point name property, which is only surfaced by eCharts in trigger: 'item' tooltips - the default trigger: 'axis' ignores it
  const hasNameAccessor = seriesConfig.type === 'scatter' && !!seriesConfig.nameAccessor
  // extraAccessors appends additional data dimensions to each point: [x, y, size|null, extra0, extra1, ...]
  const extraAccessors = seriesConfig.type === 'scatter' && Array.isArray(seriesConfig.extraAccessors) && seriesConfig.extraAccessors.length > 0
    ? seriesConfig.extraAccessors
    : null

  let minSize = Infinity
  let maxSize = -Infinity
  let sum = 0
  let sumSq = 0
  let count = 0

  const points = data.map(row => {
    const x = xAccessor ? get(row, xAccessor) : null
    const y = get(row, seriesConfig.yAccessor)
    let point
    if (!hasSizeAccessor && !extraAccessors) {
      point = [x, y]
    } else {
      let sizeVal = null
      if (hasSizeAccessor) {
        const rawSize = Number(get(row, seriesConfig.sizeAccessor) ?? NaN)
        const isFiniteSize = Number.isFinite(rawSize)
        if (isFiniteSize) {
          if (rawSize < minSize) minSize = rawSize
          if (rawSize > maxSize) maxSize = rawSize
          sum += rawSize
          sumSq += rawSize * rawSize
          count += 1
        }
        sizeVal = isFiniteSize ? rawSize : null
      }
      if (extraAccessors) {
        const extras = extraAccessors.map(e => get(row, e.accessor) ?? null)
        point = [x, y, sizeVal, ...extras]
      } else {
        point = [x, y, sizeVal]
      }
    }
    if (hasNameAccessor) {
      return { value: point, name: get(row, seriesConfig.nameAccessor) }
    }
    return point
  })

  let sizeInfo = null
  if (hasSizeAccessor && count > 0) {
    const mean = sum / count
    const variance = sumSq / count - mean * mean
    sizeInfo = { extent: [minSize, maxSize], stats: { mean, std: variance > 0 ? Math.sqrt(variance) : 0 } }
  }

  return { points, sizeInfo }
}

/**
 * Renders a composable axis chart (bar, line, scatter).
 * Multiple series compose onto shared X/Y axes within a single ChartProvider.
 */
function AxisChartLayout ({ blueprintCode, config, data, parameters, styleOverrides, definition }) {
  const {
    height = 400,
    colorTheme = 'primary',
    xAxis,
    yAxes = [],
    tooltip = {},
    series = [],
    display = {},
    overrides = null
  } = config

  const { label: displayLabel = EMPTY_OBJ, legend: displayLegend = EMPTY_OBJ, grid: displayGrid = EMPTY_OBJ } = display

  const variantStyles = styleOverrides?.styles ?? EMPTY_OBJ
  const effectiveGrid = variantStyles.grid ? { ...displayGrid, ...variantStyles.grid } : displayGrid

  const legendConfig = buildLegendConfig(displayLegend)

  const { formatter: fmtFn } = useFormattingContext()
  const labelFormatter = useCallback(
    (params) => fmtFn(params.value?.[1] ?? params.value, displayLabel.format),
    [fmtFn, displayLabel.format]
  )
  const labelConfig = useMemo(() => {
    const base = buildLabelConfig(displayLabel)
    if (displayLabel.format) return { ...base, formatter: labelFormatter }
    return base
  }, [displayLabel, labelFormatter])

  const primarySeries = useMemo(
    () => series.filter(s => !s.purposeCode || s.purposeCode === config.purposeCode),
    [series, config.purposeCode]
  )
  const overrideSeries = useMemo(
    () => series.filter(s => s.purposeCode && s.purposeCode !== config.purposeCode),
    [series, config.purposeCode]
  )

  const { points: primarySeriesData, sizeInfo } = useSeriesData(data, xAxis?.accessor, primarySeries)

  const scatterSymbolSizes = useMemo(() => {
    const result = {}
    for (const s of primarySeries) {
      if (s.type === 'scatter' && s.sizeAccessor) {
        result[s.seriesId] = computeSymbolSizeFn(s.symbolSize, s.sizeMin, s.sizeMax, sizeInfo[s.seriesId])
      }
    }
    return result
  }, [primarySeries, sizeInfo])

  const axisTooltipFormatter = useMemo(() => {
    const template = typeof tooltip.formatter === 'string' ? tooltip.formatter.trim() : ''
    if (!template) return undefined

    const map =
      tooltip.valueFormatMap &&
      typeof tooltip.valueFormatMap === 'object' &&
      !Array.isArray(tooltip.valueFormatMap) &&
      Object.keys(tooltip.valueFormatMap).length > 0
        ? tooltip.valueFormatMap
        : undefined

    const line = formatTemplate(template, map, fmtFn)

    return (params) => {
      if (Array.isArray(params)) {
        return params.map(line).join('<br/>')
      }
      return line(params)
    }
  }, [tooltip.formatter, tooltip.valueFormatMap, fmtFn])

  return (
    <ChartColorProvider theme={colorTheme}>
      <ChartProvider height={height} loadingTemplate='axis' grid={effectiveGrid} {...(legendConfig && { legend: legendConfig })} {...(styleOverrides?.props ?? {})} {...(overrides || {})}>
        {xAxis && (
          <AxisX
            type={xAxis.type || 'category'}
            position={xAxis.position || 'bottom'}
            format={xAxis.format || 'number'}
            {...(xAxis.name && { name: xAxis.name })}
            {...(xAxis.boundaryGap != null && { boundaryGap: xAxis.boundaryGap })}
            {...((xAxis.labelRotate != null || xAxis.labelInterval != null) && {
              axisLabel: {
                ...(xAxis.labelRotate != null && { rotate: xAxis.labelRotate }),
                ...(xAxis.labelInterval != null && { interval: xAxis.labelInterval })
              }
            })}
          />
        )}

        {yAxes.map((yAxis, idx) => (
          <AxisY
            key={yAxis.axisId}
            axisId={yAxis.axisId}
            type={yAxis.type || 'value'}
            position={yAxis.position || 'left'}
            format={yAxis.format || 'number'}
            padFactor={yAxis.padFactor || 0}
            {...(yAxis.name && { name: yAxis.name })}
            {...(yAxis.labelRotate != null && { axisLabel: { rotate: yAxis.labelRotate } })}
            {...(variantStyles.splitLine === false && { splitLine: { show: false } })}
          />
        ))}

        <Tooltip
          trigger={tooltip.trigger || 'axis'}
          {...(tooltip.valueFormat && { valueFormat: tooltip.valueFormat })}
          {...(axisTooltipFormatter && { formatter: axisTooltipFormatter })}
        />

        {primarySeries.map(s => {
          const Component = SERIES_COMPONENT_MAP[s.type]
          if (!Component) return null
          const computedSize = scatterSymbolSizes[s.seriesId]
          return (
            <Component
              key={s.seriesId}
              seriesId={s.seriesId}
              name={s.name}
              data={primarySeriesData[s.seriesId] || []}
              yAxisIndex={s.yAxisIndex || 0}
              {...(s.color && { itemStyle: { color: s.color } })}
              {...buildTypeSpecificProps(s, computedSize)}
              label={labelConfig}
              {...(s.overrides || {})}
            />
          )
        })}

        {overrideSeries.map(s => (
          <OverrideSeriesRenderer
            key={s.seriesId}
            blueprintCode={blueprintCode}
            seriesConfig={s}
            display={display}
            xAccessor={xAxis?.accessor}
            parameters={parameters}
            definition={definition}
          />
        ))}
      </ChartProvider>
    </ChartColorProvider>
  )
}

function buildTypeSpecificProps (s, computedSymbolSize) {
  const props = {}
  if (s.type === 'bar') {
    if (s.stack) props.stack = s.stack
    if (s.barWidth) props.barWidth = s.barWidth
  }
  if (s.type === 'line') {
    if (s.smooth) props.smooth = true
    if (s.areaStyle) props.areaStyle = {}
    if (s.step) props.step = s.step
  }
  if (s.type === 'scatter') {
    if (computedSymbolSize != null) {
      props.symbolSize = computedSymbolSize
    } else if (typeof s.symbolSize === 'number') {
      props.symbolSize = s.symbolSize
    }
    if (Array.isArray(s.extraAccessors) && s.extraAccessors.length > 0) {
      props.dimensions = ['x', 'y', s.sizeAccessor ? 'size' : '_size', ...s.extraAccessors.map(e => e.name)]
    }
  }
  return props
}

/**
 * Fetches data independently for a series that overrides the chart-level purposeCode.
 */
function OverrideSeriesRenderer ({ blueprintCode, seriesConfig, display = {}, xAccessor, parameters, definition }) {
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = useBlueprintData(
    blueprintCode,
    seriesConfig.purposeCode,
    { parameters, pageSize: 1000, definition }
  )

  useLayoutEffect(() => {
    setSeriesLoading(seriesConfig.seriesId, isLoading)
    return () => setSeriesLoading(seriesConfig.seriesId, false)
  }, [isLoading, seriesConfig.seriesId, setSeriesLoading])

  const displayLabel = display.label ?? EMPTY_OBJ
  const { formatter: fmtFn } = useFormattingContext()
  const overrideLabelConfig = useMemo(() => {
    const base = buildLabelConfig(displayLabel)
    if (displayLabel.format) {
      return { ...base, formatter: (params) => fmtFn(params.value?.[1] ?? params.value, displayLabel.format) }
    }
    return base
  }, [displayLabel, fmtFn])

  const hasSizeAccessor = seriesConfig.type === 'scatter' && seriesConfig.sizeAccessor
  const { chartData, overrideSizeInfo } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { chartData: [], overrideSizeInfo: null }
    const { points, sizeInfo } = mapSeriesPoints(data, xAccessor, seriesConfig)
    return { chartData: points, overrideSizeInfo: sizeInfo }
  }, [data, xAccessor, seriesConfig])

  const computedSize = useMemo(() => {
    if (!hasSizeAccessor) return undefined
    return computeSymbolSizeFn(seriesConfig.symbolSize, seriesConfig.sizeMin, seriesConfig.sizeMax, overrideSizeInfo)
  }, [hasSizeAccessor, seriesConfig.symbolSize, seriesConfig.sizeMin, seriesConfig.sizeMax, overrideSizeInfo])

  if (isLoading) return null

  const Component = SERIES_COMPONENT_MAP[seriesConfig.type]
  if (!Component) return null

  return (
    <Component
      seriesId={seriesConfig.seriesId}
      name={seriesConfig.name}
      data={chartData}
      yAxisIndex={seriesConfig.yAxisIndex || 0}
      {...(seriesConfig.color && { itemStyle: { color: seriesConfig.color } })}
      {...buildTypeSpecificProps(seriesConfig, computedSize)}
      label={overrideLabelConfig}
      {...(seriesConfig.overrides || {})}
    />
  )
}

OverrideSeriesRenderer.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  seriesConfig: PropTypes.object.isRequired,
  display: PropTypes.object,
  xAccessor: PropTypes.string,
  parameters: PropTypes.object,
  definition: PropTypes.object
}

AxisChartLayout.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  parameters: PropTypes.object,
  styleOverrides: PropTypes.object,
  definition: PropTypes.object
}

export default AxisChartLayout
